import createTooltip, { FOCUSABLE_SELECTOR } from '#helpers/tooltip/createTooltip.js'

const placements = [
	'top',
	'top-start',
	'top-end',
	'right',
	'right-start',
	'right-end',
	'bottom',
	'bottom-start',
	'bottom-end',
	'left',
	'left-start',
	'left-end',
]

/**
 * Translate the directive binding (modifiers + value) into tooltip options.
 * Keeps the `v-tip` contract: `v-tip="text"`, `v-tip.right.html="…"`, `v-tip.interactive="{ content, trigger, ref }"`.
 * @param {import('vue').DirectiveBinding} binding - The directive binding
 * @returns {import('#helpers/tooltip/createTooltip.js').TooltipOptions} - Options for createTooltip
 */
function optionsFromBinding(binding) {
	const modifiers = Object.keys(binding.modifiers)
	const isInteractive = modifiers.includes('interactive')

	/** @type {import('#helpers/tooltip/createTooltip.js').TooltipOptions} */
	const options = {
		allowHTML: modifiers.includes('html') || isInteractive,
		interactive: isInteractive,
		placement: modifiers.find(modifier => placements.includes(modifier)) ?? 'top',
	}

	if (typeof binding.value === 'object' && binding.value !== null) {
		const { ref, ...rest } = binding.value
		Object.assign(options, rest)
		if (ref) {
			const element = document.getElementById(ref)
			if (element) {
				options.content = element
				options.allowHTML = true
			}
		}
	} else {
		options.content = binding.value
	}

	return options
}

/**
 * Compare two directive values. Object values are compared one level deep, because a template like
 * `v-tip="{ content: text }"` creates a new object on every render even when nothing inside changed.
 * @param {unknown} value - The current binding value
 * @param {unknown} oldValue - The previous binding value
 * @returns {boolean} - Whether both values are equivalent
 */
function isSameValue(value, oldValue) {
	if (value === oldValue) {
		return true
	}
	if (
		typeof value !== 'object' ||
		typeof oldValue !== 'object' ||
		value === null ||
		oldValue === null
	) {
		return false
	}
	const keys = Object.keys(value)
	if (keys.length !== Object.keys(oldValue).length) {
		return false
	}
	return keys.every(key => value[key] === oldValue[key])
}

/**
 * Apply a (changed) binding to an existing tooltip instance.
 * @param {HTMLElement} el - The element the directive is bound to
 * @param {import('vue').DirectiveBinding} binding - The directive binding
 */
function applyBinding(el, binding) {
	const tooltip = el._tooltip
	if (!tooltip) {
		return
	}
	const options = optionsFromBinding(binding)

	// Only add a tab stop when there is something to show and no focusable ancestor (or the element itself)
	// already provides one — createTooltip listens for focus on that ancestor instead.
	// `closest` includes `el` itself, so a tabindex we added earlier must not count as a host.
	const host = el.closest(FOCUSABLE_SELECTOR)
	const hasFocusHost = host !== null && !(host === el && el.dataset.tipTabindex !== undefined)

	if (options.content && options.trigger !== 'manual' && !hasFocusHost) {
		el.tabIndex = 0
		el.dataset.tipTabindex = ''
	} else if (el.dataset.tipTabindex !== undefined) {
		el.removeAttribute('tabindex')
		delete el.dataset.tipTabindex
	}

	if (!options.content) {
		tooltip.disable()
		return
	}

	tooltip.enable()
	tooltip.setProps(options)
	tooltip.setContent(options.content)
}

/** @type {import('vue').Directive<HTMLElement & { _tooltip?: import('#helpers/tooltip/createTooltip.js').TooltipInstance }>} */
export const popoverTooltipDirective = {
	// Created in `mounted`, not `beforeMount`: the element must be in the DOM so createTooltip can find
	// a focusable ancestor (e.g. the <a> around a TruncateText) to borrow keyboard focus from.
	mounted(el, binding) {
		el._tooltip = createTooltip(el, optionsFromBinding(binding))
		applyBinding(el, binding)
	},

	updated(el, binding) {
		// Parent re-renders call this hook constantly; only re-apply when the bound value actually changed.
		if (isSameValue(binding.value, binding.oldValue)) {
			return
		}
		applyBinding(el, binding)
	},

	beforeUnmount(el) {
		el._tooltip?.destroy()
		delete el._tooltip
	},
}
